import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@contexts/AuthContext';
import { ClientAuthProvider, useClientAuth } from '@contexts/ClientAuthContext';
import Layout from '@components/layout/Layout';
import ClientLayout from '@components/layout/ClientLayout';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import Login from '@pages/Login';
import ClientLogin from '@pages/ClientLogin';
import ClientSetPassword from '@pages/ClientSetPassword';
import ClientOutcomes from '@pages/client/Outcomes';
import Dashboard from '@pages/Dashboard';
import People from '@pages/People';
import PeopleDetail from '@pages/PeopleDetail';
import PeopleForm from '@pages/PeopleForm';
import Companies from '@pages/Companies';
import CompanyDetail from '@pages/CompanyDetail';
import CompanyForm from '@pages/CompanyForm';
import Projects from '@pages/Projects';
import ProjectDetail from '@pages/ProjectDetail';
import ProjectForm from '@pages/ProjectForm';
import Settings from '@pages/Settings';
import EWTools from '@pages/EWTools';
import Docs from '@pages/Docs';
import Unsubscribe from '@pages/Unsubscribe';
import Sign from '@pages/Sign';

// ─── Protected Route ──────────────────────────────────────────────────────────

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-bg-secondary)',
      }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// ─── Client Protected Route ────────────────────────────────────────────────────
// Entirely separate check from ProtectedRoute above -- a client session must
// never satisfy an admin route or vice versa (project-brain/tech/routing.md).

const ClientProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { person, loading } = useClientAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-bg-secondary)',
      }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!person) {
    return <Navigate to="/client/login" replace />;
  }

  return <>{children}</>;
};

// ─── App Routes ───────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={<Login />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/sign/:token" element={<Sign />} />

      {/* Client portal -- public entry points */}
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/set-password" element={<ClientSetPassword />} />

      {/* Client portal -- protected, own minimal chrome (never the admin Layout) */}
      <Route
        element={
          <ClientProtectedRoute>
            <ClientLayout />
          </ClientProtectedRoute>
        }
      >
        <Route path="/client" element={<ClientOutcomes />} />
      </Route>

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/people" element={<People />} />
        <Route path="/people/new" element={<PeopleForm />} />
        <Route path="/people/:id" element={<PeopleDetail />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/companies/new" element={<CompanyForm />} />
        <Route path="/companies/:id" element={<CompanyDetail />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/new" element={<ProjectForm />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/ew-tools" element={<EWTools />} />
        <Route path="/docs" element={<Docs />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ClientAuthProvider>
          <AppRoutes />
        </ClientAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
